// Pre-payout segment building. Maps `month.events.ineligibilityReason`
// (calc-layer classification) to display labels and groups consecutive
// same-reason months within the same policy year so the UI can collapse
// them into a summary view.

import { getCumulativeIneligibleCostSeries } from '@shared/calculations/funding/projection/projectionTotals';

// Reason key for the UI. 'other' is the catch-all when calc reports null
// (shouldn't appear in the pre-payout slice by construction, but kept for
// exhaustiveness).
export type ReasonKey = ProjectionIneligibilityReason | 'other';

export interface ReasonLabel {
  key: ReasonKey;
  text: string;
  tone: string;
}

export const REASON_TOOLTIPS: Record<ReasonKey, string | null> = {
  'lt-2-adl':
    'The policy only pays once the client needs help with at least 2 of the 6 standard activities of daily living (bathing, dressing, eating, toileting, transferring, continence). During the early-care phase with fewer than 2 ADLs, the policy treats these months as ineligible — even though care costs are accruing.',
  'in-wp':
    'The elimination (waiting) period is the number of qualifying days the client must receive care before the policy starts paying. Days during this window count toward satisfying it but receive no benefit. Once the period is fully served, ongoing benefits begin.',
  other: null,
};

export function reasonForMonth(month: MonthlyEntry): ReasonLabel {
  switch (month.events.ineligibilityReason) {
    case 'lt-2-adl':
      return { key: 'lt-2-adl', text: 'Less than 2 ADLs active', tone: 'text-gray-900' };
    case 'in-wp':
      return { key: 'in-wp', text: 'Within waiting period', tone: 'text-gray-900' };
    default:
      return { key: 'other', text: '—', tone: 'text-gray-400' };
  }
}

// A group of consecutive months in the same policy year sharing the same
// reason key. Used to collapse repetitive runs ("Months 1–11: <2 ADL")
// into a single summary row.
export interface PreSegment {
  id: string;
  policyYear: number;
  reason: ReasonLabel;
  months: MonthlyEntry[];
  startMonthIndex: number;
  endMonthIndex: number;
  // Open state going *into* the first month of the segment. For an in-wp
  // segment that starts at the very beginning of WP serving, this carries
  // the configured WP days remaining — i.e. the WP total.
  openStateAtStart: MonthlyState;
  // Cumulative out-of-pocket cost at the segment's end / start. Both are
  // computed against the full projection so a downstream EP retroactive
  // lump can correctly drop these values within the same projection view.
  endCumulativeIneligible: number;
  startCumulativeIneligible: number;
  // Min/max projected cost within the segment (usually equal — cost is
  // flat within a policy year per the calc's annual stepping).
  minCost: number;
  maxCost: number;
  hasMilestones: boolean;
}

export function buildPreSegments(
  months: MonthlyEntry[],
  initialState: MonthlyState,
): PreSegment[] {
  const segments: PreSegment[] = [];
  const cumIneligibleSeries = getCumulativeIneligibleCostSeries(months);
  months.forEach((month, i) => {
    const openState = i === 0 ? initialState : months[i - 1].state;
    const reason = reasonForMonth(month);
    const prev = segments[segments.length - 1];
    // Group by reason key alone (no longer split on policy year). On products
    // where the strategy reports a calendar-stepped policy year per row
    // (Asset Flex), a multi-month WP run would otherwise split mid-segment
    // at the policy-year anniversary even though the user sees one continuous
    // waiting period. Cost variation across the segment is already captured
    // by minCost/maxCost.
    const sameSegment = prev && prev.reason.key === reason.key;

    if (sameSegment) {
      prev.months.push(month);
      prev.endMonthIndex = month.context.monthIndex;
      prev.endCumulativeIneligible = cumIneligibleSeries[i];
      prev.minCost = Math.min(prev.minCost, month.facts.effectiveCareCost);
      prev.maxCost = Math.max(prev.maxCost, month.facts.effectiveCareCost);
      prev.hasMilestones = prev.hasMilestones || month.events.milestones.length > 0;
    } else {
      segments.push({
        id: `${month.context.policyYear}-${reason.key}-${i}`,
        policyYear: month.context.policyYear,
        reason,
        months: [month],
        startMonthIndex: month.context.monthIndex,
        endMonthIndex: month.context.monthIndex,
        openStateAtStart: openState,
        startCumulativeIneligible: cumIneligibleSeries[i],
        endCumulativeIneligible: cumIneligibleSeries[i],
        minCost: month.facts.effectiveCareCost,
        maxCost: month.facts.effectiveCareCost,
        hasMilestones: month.events.milestones.length > 0,
      });
    }
  });
  return segments;
}
